<section *ngIf="editorView as rendered" class="timers" aria-label="Step timers">
  <header>
    <div>
      <h4 [id]="headingId" data-timer-heading tabindex="-1">
        Timers and escalation
      </h4>
      <p>
        Declare this visit's deadline and registered operations.
      </p>
    </div>
    <button
      uiButton
      type="button"
      (click)="refreshAvailability(rendered.origin)"
      [disabled]="disabled || loading"
    >
      Refresh availability
    </button>
  </header>
  <p *ngIf="loading" role="status">
    Loading installed operations and published calendars…
  </p>
  <p *ngIf="loadError" role="alert">{{ loadError }}</p>
  <p
    *ngIf="optionalUnavailableReason"
    role="status"
    data-timer-optional-availability
  >
    {{ optionalUnavailableReason }}
  </p>
  <p *ngIf="calendarError" role="status">{{ calendarError }}</p>
  <p *ngIf="error" role="alert" tabindex="-1">{{ error }}</p>
  <p *ngIf="ambiguous" role="alert">
    Saved timer identities are ambiguous. Their configuration is preserved for
    inspection.
  </p>
  <section *ngIf="savedShapeIssue" role="alert">
    <p>{{ savedShapeIssue }}</p>
    <pre>{{ savedConfiguration | json }}</pre>
  </section>
  <p *ngIf="!savedShapeIssue && !timers.length">
    No timer is configured for this step.
  </p>
  <article *ngFor="let timer of timers" class="timer-card">
    <header>
      <strong>{{ timerLabel(timer) }}</strong>
      <div class="actions">
        <button
          uiButton
          type="button"
          (click)="select(timer, rendered.origin)"
          [disabled]="
            disabled ||
            interactionBlocked ||
            loading ||
            ambiguous ||
            !canInspectEdit(timer)
          "
        >
          {{ canAuthor && catalog ? 'Edit timer' : 'Inspect timer' }}
        </button>
        <button
          uiButton
          type="button"
          (click)="remove(timer, rendered.origin)"
          [disabled]="
            editingDisabled ||
            ambiguous ||
            (workspaceMode && !canInspectEdit(timer))
          "
        >
          Remove timer
        </button>
      </div>
    </header>
    <p>
      This timer retains the publication's saved settings. Actual due dates
      are calculated for each visit.
    </p>
    <details *ngIf="!canInspectEdit(timer)">
      <summary>Unavailable saved configuration</summary>
      <pre>{{ timer | json }}</pre>
    </details>
  </article>
  <button
    uiButton
    type="button"
    (click)="beginPrimaryAdd(rendered.origin)"
    [disabled]="editingDisabled || ambiguous || timers.length >= 16"
  >
    Add step timer
  </button>
  <details *ngIf="runtime as availability">
    <summary>Processing availability</summary>
    <p>
      Scheduled timer actions:
      {{ availability.producerEnabled ? 'On' : 'Off' }}.
    </p>
    <p>
      Optional timer stages:
      {{
        availability.optionalStagesEnabled &&
        availability.optionalFormatExecutable
          ? 'Available'
          : 'Unavailable on this server'
      }}.
    </p>
    <p>These settings do not start work or change saved timers.</p>
  </details>
  <p *ngIf="catalog && !operations.length">
    No supported durable service operation is installed for this workflow and
    pack. Saved timers remain inspectable.
  </p>

  <section
    *ngIf="draft as timer"
    class="timer-form"
    aria-label="Timer settings"
    [attr.data-timer-version]="timer.version"
  >
    <div class="timer-settings">
      <label
        >Timer name<input
          uiControl
          maxlength="160"
          [ngModel]="timer.label"
          #timerLabel="ngModel"
          data-timer-field="label"
          (ngModelChange)="
            timerValueChanged('label', $event, rendered.origin, timerLabel)
          "
          [disabled]="editingDisabled"
      /></label>
      <label
        >Start timer<select
          uiControl
          [ngModel]="timer.startCode"
          #timerStartCode="ngModel"
          data-timer-field="startCode"
          (ngModelChange)="
            timerValueChanged(
              'startCode',
              $event,
              rendered.origin,
              timerStartCode
            )
          "
          [disabled]="editingDisabled"
        >
          <option value="">Choose start</option>
          <option value="on_entry">When this step is entered</option>
        </select></label
      >
      <label
        >Duration unit<select
          uiControl
          [ngModel]="timer.durationUnit"
          #timerUnit="ngModel"
          data-timer-field="durationUnit"
          (ngModelChange)="changeDuration($event, rendered.origin, timerUnit)"
          [disabled]="editingDisabled"
        >
          <option value="">Choose unit</option>
          <option value="elapsed_minutes">Elapsed minutes</option>
          <option value="calendar_days">Calendar days</option>
          <option value="working_days">Working days</option>
        </select></label
      >
      <label
        >Duration amount<input
          uiControl
          type="number"
          min="1"
          [max]="
            timer.durationUnit === 'elapsed_minutes'
              ? maximumMinutes
              : maximumDays
          "
          step="1"
          [ngModel]="timer.durationAmount"
          #timerDurationAmount="ngModel"
          data-timer-field="durationAmount"
          (ngModelChange)="
            timerValueChanged(
              'durationAmount',
              $event,
              rendered.origin,
              timerDurationAmount
            )
          "
          [disabled]="editingDisabled"
      /></label>
      <label *ngIf="timer.version === 2"
        >Warning<select
          uiControl
          [ngModel]="warningEnabled ? 'on' : 'off'"
          #warningMode="ngModel"
          data-timer-warning-mode
          (ngModelChange)="
            setStageEnabled('warning', $event, rendered.origin, warningMode)
          "
          [disabled]="editingDisabled"
        >
          <option value="off">Off</option>
          <option value="on">Registered operation</option>
        </select></label
      >
      <label *ngIf="timer.version === 2"
        >Overdue operation<select
          uiControl
          [ngModel]="overdueEnabled ? 'on' : 'off'"
          #overdueMode="ngModel"
          data-timer-overdue-mode
          (ngModelChange)="
            setStageEnabled('breach', $event, rendered.origin, overdueMode)
          "
          [disabled]="editingDisabled"
        >
          <option value="off">None</option>
          <option value="on">Registered operation</option>
        </select></label
      >
      <label *ngIf="stageEnabled('warning')"
        >{{
          timer.version === 2
            ? 'Warning before deadline (minutes)'
            : 'Warning before breach (minutes)'
        }}<input
          uiControl
          type="number"
          min="1"
          [max]="maximumMinutes"
          step="1"
          [ngModel]="timer.warningLeadMinutes"
          #timerWarningLeadMinutes="ngModel"
          data-timer-field="warningLeadMinutes"
          (ngModelChange)="
            timerValueChanged(
              'warningLeadMinutes',
              $event,
              rendered.origin,
              timerWarningLeadMinutes
            )
          "
          [disabled]="editingDisabled"
      /></label>
      <label
        >Pause rule<select
          uiControl
          [ngModel]="timer.pauseCode"
          #timerPauseCode="ngModel"
          data-timer-field="pauseCode"
          (ngModelChange)="
            timerValueChanged(
              'pauseCode',
              $event,
              rendered.origin,
              timerPauseCode
            )
          "
          [disabled]="editingDisabled"
        >
          <option value="">Choose pause rule</option>
          <option value="disabled">Pausing unavailable</option>
          <option value="manual_before_warning">
            Allow manual pause before warning
          </option>
        </select></label
      >
      <label
        >Reset rule<select
          uiControl
          [ngModel]="timer.resetCode"
          #timerResetCode="ngModel"
          data-timer-field="resetCode"
          (ngModelChange)="
            timerValueChanged(
              'resetCode',
              $event,
              rendered.origin,
              timerResetCode
            )
          "
          [disabled]="editingDisabled"
        >
          <option value="">Choose reset rule</option>
          <option value="disabled">Reset unavailable</option>
          <option value="restart_from_now">
            Allow a fresh timer from reset time
          </option>
        </select></label
      >
      <label
        >When the step is revisited<select
          uiControl
          [ngModel]="timer.reentryCode"
          #timerReentryCode="ngModel"
          data-timer-field="reentryCode"
          (ngModelChange)="
            timerValueChanged(
              'reentryCode',
              $event,
              rendered.origin,
              timerReentryCode
            )
          "
          [disabled]="editingDisabled"
        >
          <option value="">Choose revisit rule</option>
          <option value="restart_new_occurrence">
            Start a new timer for the new visit
          </option>
        </select></label
      >
    </div>
    <fieldset
      *ngIf="dayBased"
      [disabled]="editingDisabled"
      class="timer-settings"
    >
      <legend>Calendar and local due time</legend>
      <label
        >Published calendar<select
          uiControl
          [ngModel]="calendarSelection"
          #timerCalendar="ngModel"
          data-timer-field="calendar"
          (ngModelChange)="
            chooseCalendar($event, rendered.origin, timerCalendar)
          "
        >
          <option value="">Choose a published version</option>
          <option *ngIf="retainedCalendar" [value]="calendarSelection">
            Saved version unavailable · retained
          </option>
          <option
            *ngFor="let calendar of calendars"
            [value]="calendarIdentity(calendar)"
          >
            {{ calendar.label }} · {{ calendar.versionCode }} ·
            {{ calendar.timeZoneId }}
          </option>
        </select></label
      >
      <label
        >Local due time<input
          uiControl
          type="time"
          [ngModel]="timer.dueLocalTime"
          #timerTime="ngModel"
          data-timer-field="dueLocalTime"
          (ngModelChange)="
            timerValueChanged(
              'dueLocalTime',
              $event,
              rendered.origin,
              timerTime
            )
          "
      /></label>
      <label
        >Missing local time<select
          uiControl
          [ngModel]="timer.gapCode"
          #timerGapCode="ngModel"
          data-timer-field="gapCode"
          (ngModelChange)="
            timerValueChanged('gapCode', $event, rendered.origin, timerGapCode)
          "
        >
          <option [ngValue]="null">Choose gap rule</option>
          <option value="reject">Reject a daylight-saving gap</option>
        </select></label
      >
      <label
        >Repeated local time<select
          uiControl
          [ngModel]="timer.overlapCode"
          #timerOverlapCode="ngModel"
          data-timer-field="overlapCode"
          (ngModelChange)="
            timerValueChanged(
              'overlapCode',
              $event,
              rendered.origin,
              timerOverlapCode
            )
          "
        >
          <option [ngValue]="null">Choose overlap rule</option>
          <option value="reject">Reject an ambiguous time</option>
          <option value="earlier">Use the earlier occurrence</option>
          <option value="later">Use the later occurrence</option>
        </select></label
      >
    </fieldset>
    <p class="hint">
      Pause freezes remaining elapsed time before warning. Resume shifts the
      retained due instants; reset creates a new generation. A due date alone
      performs no release or workflow transition.
    </p>
    <fieldset
      *ngFor="let stage of stages"
      [attr.data-timer-stage]="stage"
      [hidden]="!stageEnabled(stage)"
      [disabled]="editingDisabled || !stageEnabled(stage)"
      class="timer-stage"
    >
      <ng-container *ngIf="stageView(stage) as stageRendered">
        <legend>
          {{
            stage === 'warning'
              ? 'Warning operation'
              : timer.version === 2
                ? 'Overdue operation'
                : 'Breach operation'
          }}
        </legend>
        <label
          >Registered operation<select
            uiControl
            [ngModel]="operationId(stage)"
            #timerOperation="ngModel"
            (ngModelChange)="
              selectOperation(
                stage,
                $event,
                stageRendered.origin,
                timerOperation
              )
            "
          >
            <option value="">Choose an installed operation</option>
            <option
              *ngIf="stageAction(stage) && !operationAvailable(stage)"
              [value]="operationId(stage)"
            >
              Unavailable saved operation · retained
            </option>
            <option
              *ngFor="let item of operations"
              [disabled]="!!operationPolicyError(item)"
              [value]="
                item.command.key.provider +
                '/' +
                item.command.key.kind +
                '/' +
                item.command.key.code +
                '/' +
                item.command.key.version
              "
            >
              {{ item.label }} · v{{ item.command.key.version
              }}{{
                operationPolicyError(item) ? ' · unavailable for this step' : ''
              }}
            </option>
          </select></label
        >
        <fieldset
          *ngIf="stageAction(stage) as action"
          [disabled]="stageDisabled(stage)"
          class="stage-inputs"
        >
          <label
            >On operation failure<select
              uiControl
              [ngModel]="action.failurePolicy"
              #timerFailure="ngModel"
              (ngModelChange)="
                failureChanged(
                  stage,
                  $event,
                  stageRendered.origin,
                  timerFailure
                )
              "
            >
              <option value="">Choose failure rule</option>
              <option value="stop">Stop this action and report failure</option>
              <option value="continue">
                Record failure and continue the group
              </option>
            </select></label
          >
          <ng-container *ngIf="operation(stage) as item">
            <section
              *ngFor="let field of item.input.fields"
              class="timer-input"
              [attr.data-timer-input]="field.name"
            >
              <ng-container *ngIf="stageView(stage, field) as inputRendered">
                <strong
                  >{{ field.name
                  }}{{ field.required ? ' (required)' : '' }}</strong
                >
                <label
                  >Value source<select
                    uiControl
                    [ngModel]="binding(stage, field)?.source || ''"
                    #timerSource="ngModel"
                    (ngModelChange)="
                      setSource(
                        stage,
                        field,
                        $event,
                        inputRendered.origin,
                        timerSource
                      )
                    "
                  >
                    <option value="">
                      {{ field.required ? 'Choose source' : 'Not supplied' }}
                    </option>
                    <option value="literal">Configured value</option>
                    <option
                      value="context"
                      [disabled]="!contextChoices(field).length"
                    >
                      Captured process context
                    </option>
                  </select></label
                >
                <label *ngIf="binding(stage, field)?.source === 'context'"
                  >Process value<select
                    uiControl
                    [ngModel]="binding(stage, field)?.context || ''"
                    #timerContext="ngModel"
                    (ngModelChange)="
                      setContext(
                        stage,
                        field,
                        $event,
                        inputRendered.origin,
                        timerContext
                      )
                    "
                  >
                    <option value="">Choose compatible value</option>
                    <option
                      *ngFor="let value of contextChoices(field)"
                      [value]="value.code"
                    >
                      {{ value.label }}
                    </option>
                  </select></label
                >
                <ng-container
                  *ngIf="binding(stage, field)?.source === 'literal'"
                >
                  <label
                    *ngIf="
                      field.type === 'number' || field.type === 'integer';
                      else ordinaryInput
                    "
                    >Configured number<input
                      uiControl
                      type="text"
                      inputmode="decimal"
                      maxlength="128"
                      [ngModel]="exactLiteral(stage, field)"
                      #timerExact="ngModel"
                      (ngModelChange)="
                        setExactLiteral(
                          stage,
                          field,
                          $event,
                          inputRendered.origin,
                          timerExact
                        )
                      "
                  /></label>
                  <button
                    *ngIf="
                      field.nullable &&
                      (field.type === 'number' || field.type === 'integer')
                    "
                    uiButton
                    type="button"
                    (click)="
                      setExactLiteral(
                        stage,
                        field,
                        'null',
                        inputRendered.origin
                      )
                    "
                  >
                    Use no value
                  </button>
                  <ng-template #ordinaryInput>
                    <ng-container *ngIf="workspaceMode; else legacyInput">
                      <select
                        *ngIf="field.values?.length; else primitiveInput"
                        uiControl
                        [attr.aria-label]="field.name"
                        [ngModel]="binding(stage, field)?.value"
                        #timerEnum="ngModel"
                        [disabled]="stageDisabled(stage)"
                        (ngModelChange)="
                          setLiteralValue(
                            stage,
                            field,
                            $event,
                            inputRendered.origin,
                            timerEnum
                          )
                        "
                      >
                        <option [ngValue]="undefined">Choose a value</option>
                        <option
                          *ngFor="let value of field.values"
                          [value]="value"
                        >
                          {{ value }}
                        </option>
                      </select>
                      <ng-template #primitiveInput>
                        <select
                          *ngIf="field.type === 'boolean'; else stringInput"
                          uiControl
                          [attr.aria-label]="field.name"
                          [ngModel]="binding(stage, field)?.value"
                          #timerBoolean="ngModel"
                          [disabled]="stageDisabled(stage)"
                          (ngModelChange)="
                            setLiteralValue(
                              stage,
                              field,
                              $event,
                              inputRendered.origin,
                              timerBoolean
                            )
                          "
                        >
                          <option [ngValue]="undefined">Choose a value</option>
                          <option [ngValue]="true">True</option>
                          <option [ngValue]="false">False</option>
                        </select>
                        <ng-template #stringInput
                          ><input
                            uiControl
                            type="text"
                            [attr.aria-label]="field.name"
                            [attr.maxlength]="field.maxLength"
                            [ngModel]="binding(stage, field)?.value"
                            #timerText="ngModel"
                            [disabled]="stageDisabled(stage)"
                            (ngModelChange)="
                              setLiteralValue(
                                stage,
                                field,
                                $event,
                                inputRendered.origin,
                                timerText
                              )
                            "
                        /></ng-template>
                      </ng-template>
                      <button
                        *ngIf="field.nullable"
                        uiButton
                        type="button"
                        (click)="
                          setLiteralValue(
                            stage,
                            field,
                            null,
                            inputRendered.origin
                          )
                        "
                      >
                        Use no value
                      </button>
                    </ng-container>
                    <ng-template #legacyInput
                      ><phms-typed-capability-inputs
                        [fields]="[field]"
                        [values]="literalValues(stage, field)"
                        [disabled]="stageDisabled(stage)"
                        (valuesChange)="
                          setLiteralValues(
                            stage,
                            field,
                            $event,
                            inputRendered.origin
                          )
                        "
                    /></ng-template>
                  </ng-template>
                </ng-container>
              </ng-container>
            </section>
          </ng-container>
          <p *ngIf="!operationAvailable(stage)" role="status">
            {{ stageUnavailableReason(stage) }}
          </p>
        </fieldset>
      </ng-container>
    </fieldset>
    <div class="actions">
      <button
        uiButton
        type="button"
        severity="primary"
        [outlined]="false"
        (click)="apply(rendered.origin)"
        [disabled]="editingDisabled"
      >
        Apply timer</button
      ><button
        uiButton
        type="button"
        (click)="discardVisibleChanges(rendered.origin)"
      >
        Discard timer edits</button
      ><button
        *ngIf="adding && exampleAvailable"
        uiButton
        type="button"
        (click)="fillExample(rendered.origin)"
        [disabled]="editingDisabled || !exampleAvailable"
      >
        Fill synthetic numeric example
      </button>
    </div>
    <p *ngIf="adding && !exampleAvailable && timer.version !== 2" role="status">
      The synthetic numeric example is unavailable for this workflow and pack.
    </p>
    <p *ngIf="exampleAvailable" class="hint">
      The synthetic example uses the real installed numeric calculation. It
      sends no message. Worker configuration and current service authority are
      required separately for execution.
    </p>
  </section>
</section>
